import React, { PureComponent } from 'react';
import { Button, CircularProgress } from '@mic3/platform-ui';
import genUuidv4 from 'uuid/v4';

import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';
import ModalDialog from 'app/components/organisms/ModalDialog/ModalDialog';
import { bind } from 'app/utils/decorators/decoratorUtils';
import { capitalizeFirstLetter } from 'app/utils/utils';
import { widgetTypes, widgetDefinitions } from '../dashboardUtils';
import { isValidURL } from 'app/utils/string/string-utils';

const moduleWidgetOptions = Object.keys(widgetDefinitions).map((key) => ({
    value: widgetDefinitions[key].header,
    label: widgetDefinitions[key].header,
    permission: widgetDefinitions[key].permission,
}));

const userBasedWidgetOptions = [
    { label: 'Tasks Due Soon', value: 'tasksByDueDate', permission: 'abox.task' },
    { label: 'New Tasks', value: 'tasksByCreatedDate', permission: 'abox.task' },
    { label: 'Highest Priority Tasks', value: 'tasksByPriority', permission: 'abox.task' },
    { label: 'Last Updated Tasks', value: 'lastUpdatedTasks', permission: 'abox.task' },
    { label: 'Maps', value: 'userMaps', permission: 'maps.sa' },
];

const getAccessibleOptions = (options, profile) => {
    const { isAdmin, permissions } = profile || {};
    if (isAdmin) return options;
    const permissionsSet = new Set(permissions || []);
    return options?.filter((opt) =>
        Array.isArray(opt.permission)
            ? opt.permission.some((permission) => permissionsSet.has(permission))
            : permissionsSet.has(opt.permission)
    );
};

class AddWidgetModal extends PureComponent {
    formRef = React.createRef();
    state = {
        formData: this.props.widget?.data,
    };

    @bind
    buildModalComponents() {
        const { type, userProfile } = this.props;
        const userSpOptions = getAccessibleOptions(userBasedWidgetOptions, userProfile);
        const moduleOptions = getAccessibleOptions(moduleWidgetOptions, userProfile);
        return [
            type !== widgetTypes.module && {
                type: 'text',
                properties: { label: 'Widget Name', name: 'widgetName' },
                constraints: { required: true },
            },
            type === widgetTypes.link && {
                type: 'groupRepeat',
                children: [
                    {
                        type: 'hyperlink',
                        properties: {
                            name: 'link',
                            label: '',
                            hideTickIcon: true,
                        },
                        constraints: {
                            required: true,
                        },
                    },
                ],
                properties: {
                    name: 'linksData',
                    addButtons: true,
                    // "addDividers": true,
                    // "rowDirection": false
                },
            },
            type === widgetTypes.user && {
                type: 'typeahead',
                properties: {
                    label: 'User Based Widgets',
                    name: 'selectedWidget',
                    valueField: 'value',
                    options: userSpOptions,
                    placeholder: 'Select a user based widgets.',
                },
                constraints: { required: true },
            },
            type === widgetTypes.module && {
                type: 'typeahead',
                properties: {
                    label: 'System Module',
                    name: 'selectedModule',
                    valueField: 'value',
                    options: moduleOptions,
                    placeholder: 'Select a system module.',
                },
                constraints: { required: true },
            },
            type === widgetTypes.map && {
                type: 'entityTypeahead',
                properties: {
                    label: 'Select a map',
                    name: 'map',
                    showType: false,
                    placeholder: 'Select a map...',
                    entityType: 'system_map',
                    filterBy: [
                        {
                            field: 'active',
                            op: '=',
                            value: true,
                        },
                    ],
                },
                constraints: {
                    required: true,
                },
            },
            type === widgetTypes.iframe && {
                type: 'text',
                properties: { label: 'URL', name: 'url' },
                constraints: {
                    required: true,
                    custom: (data) => {
                        const message = { format: { pattern: /somefaketest/, message: 'Provided URL is not valid' } };
                        if (!isValidURL(data.url)) {
                            return message;
                        }
                        return {};
                    },
                },
            },
            type === widgetTypes.calendar && {
                type: 'typeahead',
                properties: {
                    label: 'Default View',
                    name: 'defaultView',
                    options: [
                        {
                            value: 'day',
                            label: 'Day',
                        },
                        {
                            value: 'week',
                            label: 'Week',
                        },
                        {
                            value: 'month',
                            label: 'Month',
                        },
                    ],
                },
                constraints: { required: true },
            },
        ].filter(Boolean);
    }

    @bind
    handleFormChange(formData) {
        this.setState({ formData });
    }

    @bind
    onFormSubmit(event) {
        event.preventDefault();
        const { onAddWidget, onUpdateWidget, onClose, type, widget } = this.props;
        const isEditing = !!widget;
        const id = isEditing ? widget.id : genUuidv4();
        const action = isEditing ? onUpdateWidget : onAddWidget;
        this.formRef.current.isValidForm().then(({ data, errors }) => {
            if (!errors) {
                if (type === widgetTypes.map) {
                    const { id, name } = data.map || {};
                    const map = { id, name };
                    data = { map, widgetName: data.widgetName };
                }
                action({ data, id, type });
                onClose();
            }
        });
    }

    render() {
        const { isLoading, onClose, type, widget } = this.props;
        const { formData } = this.state;
        const titlePrefix = widget?.data ? 'Edit' : 'Add';
        const title = type === 'userApps' ? 'My Apps' : `${titlePrefix} ${capitalizeFirstLetter(type)} Widget`;
        return (
            <ModalDialog
                title={title}
                onClose={onClose}
                actions={isLoading ? <CircularProgress size={24} color='primary' /> : <Button onClick={this.onFormSubmit}>Apply</Button>}
            >
                <FormGenerator
                    components={this.buildModalComponents()}
                    ref={this.formRef}
                    data={formData}
                    onChange={this.handleFormChange}
                />
            </ModalDialog>
        );
    }
}

export default AddWidgetModal;
